/**
 * One posted update and its replies (`specs/M1-B7-item-card-and-updates.md` §2.3).
 *
 * Captured geometry: a 668-wide bordered card, a 30 × 30 avatar at x 781, the creator in bold
 * 14 px with the relative time after it, three 24 × 24 icons at the right, `Like` 65 × 32 and
 * `Reply` 75 × 32 over a divider, a 40 × 24 like pill with `border-radius: 100px`, and the
 * reply composer **always visible** underneath at 602 × 55 `[ic§A posted update]`.
 *
 * **Round 6g closed q254 by pinning and liking a real update on the sandbox**
 * (`q254-pinned.png`, `q254-liked.png`), and both readings B7 shipped were wrong:
 *
 *  - a pinned update grows **no band**. It grows a **`Pinned` chip, 81 × 24 @ (1244, 397)** —
 *    background `rgb(204,229,255)`, text `rgb(50,51,56)` — in its header row, immediately
 *    left of the three icons. The card's border and background do not change, and the update
 *    is **not** moved by the server (q248); the client sorts pinned first;
 *  - a **liked** update does not colour the word `Like`. The 65 × 32 `Like` control is
 *    *replaced* by a **36 × 32 chip carrying the reaction emoji**, and the count pill above
 *    the action row reads **`<emoji> <n>`**, not the bare number.
 */
import { Icon, icons } from '@elizmonday/ui';
import { useEffect, useRef, useState } from 'react';
import { fileSize, relativeTime, updateMenuRows, updatePermalink } from './composer-model';
import { Composer } from './Composer';
import type { CardAsset, CardMention, CardPerson, CardUpdate, CardUpdateReader } from './types';

export interface UpdateCardProps {
  update: CardUpdate;
  boardId: string;
  itemId: string;
  me: CardPerson | null;
  depth?: 0 | 1;
  mentionCandidates(query: string): Promise<CardPerson[]>;
  /** P-299: `attachments` are already-uploaded assets, linked to the reply once it exists. */
  onReply(input: { parentId: string; body: string; mentions: CardMention[]; attachments: CardAsset[] }): Promise<void>;
  onEdit(input: { updateId: string; body: string; mentions: CardMention[]; attachments: CardAsset[] }): Promise<void>;
  onDelete(updateId: string): Promise<void>;
  onToggleLike(update: CardUpdate): Promise<void>;
  onTogglePin(update: CardUpdate): Promise<void>;
  /**
   * P-299: uploads one pasted, dropped or picked file **now** and hands back the asset. It
   * names no update, because the reply or the edit it belongs to may not be posted yet — the
   * composer holds it in its pending strip and the card links it on submit.
   */
  onAttach(file: File): Promise<CardAsset | null>;
  /**
   * **P-317** — who has seen this update. Asked for when the `👁 n` is hovered, focused or
   * tapped, never with the page (see `ItemCardSource.updateReaders`). Absent on a source that
   * cannot answer it, which leaves the counter the plain label it has always been.
   */
  onReaders?(input: { updateId: string }): Promise<CardUpdateReader[]>;
}

/** monday's default reaction, the one a plain `like_update` writes `[ua:a14]`. */
const LIKE_EMOJI = '\u{1F44D}';

/** An `update_assets` row monday would paint rather than link (P-028). */
const isImage = (mime: string): boolean => /^image\//i.test(mime) && !/^image\/svg/i.test(mime);

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/**
 * **P-317 — the `👁 n` says *who*** (Antonino, 2026-10-06: *"on hover o click sull'occhio sapere
 * chi l'ha visto"*).
 *
 * **monday has nothing to copy here.** The round-11l capture went looking for a "seen by"
 * affordance on a real update and found none: monday's item-card update footer carries `Like` and
 * `Reply` and then 494 px of empty filler, and neither `/feed` nor `/notifications` has anything
 * eye-like either (`discovery/ui-reference/item-card/11l-update-footer.json`,
 * `_notes-round11l.json`). Our `👁 n` itself comes from a parity screenshot (P-029) and its number
 * from `count(update_reads)` (T-8d).
 *
 * So this is the **smallest honest version**, and it is **ASSUMED** (q598): the counter becomes a
 * button, and the gesture opens a list of the readers the server already serves — avatar, name,
 * and when they read it — in read order, the author excluded (P-315). What is *not* invented:
 *
 *  - **no cap and no `+n more`.** A cap is a number nobody measured; the list scrolls instead.
 *  - **no wording monday uses.** `Seen by` is the heading, which is the plainest English for what
 *    the rows are, and a refusal prints **the api's own sentence** rather than one of ours.
 *  - **the three gestures the card asks for, and nothing more**: hover, focus and tap/click. Hover
 *    and focus open it; the pointer leaving the control *and* the list closes it; a click toggles
 *    it, which is what makes a tap work on a phone where there is no hover at all; `Escape` closes.
 */
function SeenBy({
  update,
  onReaders,
}: {
  update: CardUpdate;
  onReaders(input: { updateId: string }): Promise<CardUpdateReader[]>;
}) {
  const [open, setOpen] = useState(false);
  /** `null` while it has never been asked for — the first gesture is what fetches it. */
  const [readers, setReaders] = useState<CardUpdateReader[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** A click must not be undone by the `mouseleave` the same gesture produces on a phone. */
  const pinned = useRef(false);
  const asked = useRef(false);
  const alive = useRef(true);

  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const load = () => {
    if (asked.current) return;
    asked.current = true;
    setLoading(true);
    void onReaders({ updateId: update.id })
      .then((rows) => {
        if (!alive.current) return;
        setReaders(rows);
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (!alive.current) return;
        // The api's own sentence, never one of ours (the rule `/files/upload` learnt in P-299).
        asked.current = false;
        setFailure(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  };

  const show = () => {
    load();
    setOpen(true);
  };

  const hide = () => {
    if (pinned.current) return;
    setOpen(false);
  };

  return (
    <span
      className="ez-update__views-host"
      onMouseEnter={show}
      onMouseLeave={hide}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        pinned.current = false;
        setOpen(false);
      }}
    >
      <button
        type="button"
        className="ez-update__views"
        data-testid={`update-${update.id}-views`}
        aria-label={`Seen by ${String(update.viewsCount ?? 0)}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onFocus={show}
        onBlur={() => {
          pinned.current = false;
          setOpen(false);
        }}
        onClick={() => {
          if (open && pinned.current) {
            pinned.current = false;
            setOpen(false);
            return;
          }
          pinned.current = true;
          show();
        }}
      >
        <Icon icon={icons.show} size={16} ignoreFocusStyle />
        {update.viewsCount}
      </button>
      {open ? (
        <span
          className="ez-update__seen"
          role="dialog"
          aria-label="Seen by"
          data-testid={`update-${update.id}-seen-by`}
        >
          <span className="ez-update__seen-title">Seen by</span>
          {failure !== null ? (
            <span className="ez-update__seen-empty" data-testid={`update-${update.id}-seen-by-error`}>
              {failure}
            </span>
          ) : loading && readers === null ? (
            <span className="ez-update__seen-empty">Loading…</span>
          ) : readers === null || readers.length === 0 ? (
            /* Never a silent empty state (the round-4 rule): the control said `0`, this says why. */
            <span className="ez-update__seen-empty" data-testid={`update-${update.id}-seen-by-empty`}>
              No one has seen this yet
            </span>
          ) : (
            <ul className="ez-update__seen-list">
              {readers.map((reader) => (
                <li
                  key={reader.id}
                  className="ez-update__seen-row"
                  data-testid={`update-${update.id}-seen-by-${reader.id}`}
                >
                  <span className="ez-update__seen-avatar" aria-hidden="true">
                    {reader.photoUrl ? <img src={reader.photoUrl} alt="" width={24} height={24} /> : initials(reader.name)}
                  </span>
                  <span className="ez-update__seen-name">{reader.name}</span>
                  <time className="ez-update__seen-at" dateTime={reader.readAt}>
                    {relativeTime(reader.readAt)}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </span>
      ) : null}
    </span>
  );
}

export function UpdateCard(props: UpdateCardProps) {
  const { update, boardId, itemId, me, depth = 0 } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const liked = me !== null && update.likes.some((like) => like.userId === me.id);

  const close = () => setMenuOpen(false);

  return (
    <article
      className={`ez-update ez-update--depth-${String(depth)}${update.pinned ? ' ez-update--pinned' : ''}`}
      data-testid={`update-${update.id}`}
      data-pinned={update.pinned ? 'true' : 'false'}
    >
      <header className="ez-update__header">
        <span className="ez-update__avatar" aria-hidden="true">
          {initials(update.creator?.name ?? '?')}
        </span>
        <span className="ez-update__author" data-testid={`update-${update.id}-author`}>
          {update.creator?.name ?? 'Unknown'}
        </span>
        <time className="ez-update__time" dateTime={update.createdAt} data-testid={`update-${update.id}-time`}>
          {relativeTime(update.createdAt)}
        </time>
        {update.editedAt ? (
          <span className="ez-update__edited" data-testid={`update-${update.id}-edited`}>
            Edited
          </span>
        ) : null}

        {/*
          * P-029: monday draws **three** 24 x 24 controls here, not one `[ic§A posted update]`.
          * Its first two are `Summarize update` (its own model) and `Set reminder` (its own
          * scheduler); neither exists in this product, so the slots hold the two actions that
          * do and that were otherwise buried in the `⋯` menu — edit, and copy link.
          */}
        <div className="ez-update__actions">
          {/* q254: the pinned chip, 81 × 24, left of the icons. */}
          {update.pinned && depth === 0 ? (
            <span className="ez-update__pinned-chip" data-testid={`update-${update.id}-pinned-chip`}>
              Pinned
            </span>
          ) : null}
          <button
            type="button"
            className="ez-update__icon"
            aria-label={`Edit the update by ${update.creator?.name ?? 'unknown'}`}
            title="Edit update"
            data-testid={`update-${update.id}-edit-button`}
            onClick={() => setEditing(true)}
          >
            <Icon icon={icons.description} size={16} ignoreFocusStyle />
          </button>
          <button
            type="button"
            className="ez-update__icon"
            aria-label={`Copy a link to the update by ${update.creator?.name ?? 'unknown'}`}
            title="Copy link to update"
            data-testid={`update-${update.id}-copy-link`}
            onClick={() => {
              void navigator.clipboard
                ?.writeText(updatePermalink(boardId, itemId, update.id, window.location.origin))
                .catch(() => undefined);
            }}
          >
            <Icon icon={icons.link} size={16} ignoreFocusStyle />
          </button>
          <button
            type="button"
            className="ez-update__icon"
            aria-label={`Menu for the update by ${update.creator?.name ?? 'unknown'}`}
            data-testid={`update-${update.id}-menu-button`}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            ⋯
          </button>
        </div>

        {menuOpen ? (
          <div className="ez-update__menu" role="menu" data-testid={`update-${update.id}-menu`}>
            {updateMenuRows(update).map((row) => (
              <button
                key={row.id}
                type="button"
                role="menuitem"
                className={`ez-update__menu-row${row.divider ? ' ez-update__menu-row--divided' : ''}`}
                data-testid={`update-${update.id}-menu-${row.id}`}
                onClick={() => {
                  close();
                  if (row.id === 'edit') setEditing(true);
                  if (row.id === 'pin') void props.onTogglePin(update);
                  if (row.id === 'delete') void props.onDelete(update.id);
                  if (row.id === 'copy-link') {
                    void navigator.clipboard
                      ?.writeText(updatePermalink(boardId, itemId, update.id, window.location.origin))
                      .catch(() => undefined);
                  }
                }}
              >
                {row.label}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      {editing ? (
        <Composer
          variant="edit"
          boardId={boardId}
          itemId={itemId}
          me={me}
          initialHtml={update.bodyHtml}
          // q253, measured: the edit composer's primary button reads **Save**, not `Update`,
          // and it sits next to `Cancel` inside a 632-wide bordered box that replaces the
          // body while the header (avatar + author, no timestamp) stays `[ic§q253-edit-in-place]`.
          submitLabel="Save"
          testId={`update-${update.id}-editor`}
          mentionCandidates={props.mentionCandidates}
          onCancel={() => setEditing(false)}
          onSubmit={async ({ body, mentions, attachments }) => {
            await props.onEdit({ updateId: update.id, body, mentions, attachments });
            setEditing(false);
          }}
          onAttach={props.onAttach}
        />
      ) : (
        <div
          className="ez-update__body"
          data-testid={`update-${update.id}-body`}
          // The body is server-sanitised (`sanitizeUpdateBody`, B7 §6): a fixed tag allow-list,
          // http/https/mailto hrefs only and images restricted to our own `/files/` route.
          dangerouslySetInnerHTML={{ __html: update.bodyHtml }}
        />
      )}

      {/*
        * P-028: monday renders an **image** attachment inline, at the update body's own width
        * (~460 px in the parity shot on item 11752987312), directly under the body — the
        * file-name link is the fallback for everything that is not an image. The bytes are
        * already on disk: round 5y's import copied 6 472 / 6 472 assets.
        */}
      {update.assets.length > 0 ? (
        <ul className="ez-update__assets" data-testid={`update-${update.id}-assets`}>
          {update.assets.map((asset) =>
            isImage(asset.mime) ? (
              <li key={asset.id} className="ez-update__asset-image">
                <a href={asset.url} target="_blank" rel="noopener noreferrer" data-testid={`asset-${asset.id}`}>
                  <img
                    src={asset.thumbnailUrl ?? asset.url}
                    alt={asset.name}
                    loading="lazy"
                    data-testid={`asset-${asset.id}-image`}
                  />
                </a>
              </li>
            ) : (
              <li key={asset.id}>
                <a href={asset.url} className="ez-update__asset" download data-testid={`asset-${asset.id}`}>
                  {asset.name}
                  <span className="ez-update__asset-size">{fileSize(asset.size)}</span>
                </a>
              </li>
            ),
          )}
        </ul>
      ) : null}

      {update.likes.length > 0 ? (
        <div className="ez-update__likes">
          <button
            type="button"
            className="ez-update__like-pill"
            data-testid={`update-${update.id}-like-count`}
            onClick={() => void props.onToggleLike(update)}
          >
            <span aria-hidden="true">{LIKE_EMOJI}</span>
            {update.likes.length}
          </button>
        </div>
      ) : null}

      <footer className="ez-update__footer">
        {/*
          * q254: liked is a **different control**, not a coloured label — 36 × 32 with the
          * reaction emoji where the 65 × 32 `Like` was.
          */}
        <button
          type="button"
          className={`ez-update__action${liked ? ' ez-update__action--reacted' : ''}`}
          data-testid={`update-${update.id}-like`}
          aria-pressed={liked}
          aria-label={liked ? 'Remove your reaction' : 'Like'}
          onClick={() => void props.onToggleLike(update)}
        >
          {liked ? (
            LIKE_EMOJI
          ) : (
            <>
              <Icon icon={icons.like} size={16} ignoreFocusStyle />
              Like
            </>
          )}
        </button>
        {/* A reply has no `Reply` of its own: monday's thread is exactly two deep `[ua:a11]`. */}
        {depth === 0 ? (
          <span className="ez-update__action ez-update__action--static" data-testid={`update-${update.id}-reply`}>
            <Icon icon={icons.reply} size={16} ignoreFocusStyle />
            Reply
          </span>
        ) : null}
        {/*
          * P-029: `👁 n` sits at the right of the footer. Since T-8d the server counts it —
          * `count(update_reads)`, the readers the Update feed has marked — so the control
          * renders on real data too; a source that sends no count keeps it absent rather than
          * printing a zero nobody measured.
          *
          * P-317: and with a source that can name those readers it is a **button** that opens
          * the list on hover, focus or tap (`SeenBy`). Without one it stays the label it was,
          * so no surface loses the number because it cannot answer the new question.
          */}
        {typeof update.viewsCount !== 'number' ? null : props.onReaders ? (
          <SeenBy update={update} onReaders={props.onReaders} />
        ) : (
          <span className="ez-update__views" data-testid={`update-${update.id}-views`}>
            <Icon icon={icons.show} size={16} ignoreFocusStyle />
            {update.viewsCount}
          </span>
        )}
      </footer>

      {depth === 0 ? (
        <div className="ez-update__replies" data-testid={`update-${update.id}-replies`}>
          {update.replies.map((reply) => (
            <UpdateCard key={reply.id} {...props} update={reply} depth={1} />
          ))}
          <Composer
            variant="reply"
            boardId={boardId}
            itemId={itemId}
            me={me}
            placeholder="Write a reply and mention others with @"
            submitLabel="Reply"
            testId={`update-${update.id}-reply-composer`}
            mentionCandidates={props.mentionCandidates}
            onSubmit={({ body, mentions, attachments }) =>
              props.onReply({ parentId: update.id, body, mentions, attachments })
            }
            onAttach={props.onAttach}
          />
        </div>
      ) : null}
    </article>
  );
}
